import { RoryQuote } from "./types";

const BRAND = {
  cream: "#F5F0E1",
  plum: "#2E2437",
  sunshine: "#F3EC7A",
  white: "#FFFFFF",
};

// 4:5 portrait, the shape Instagram and TikTok feeds like.
const W = 1080;
const H = 1350;

const roundRect = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const wrapLines = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] => {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = test;
    }
  }
  if (current) lines.push(current);
  return lines;
};

const loadImage = (src: string): Promise<HTMLImageElement | null> =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

// Draws a branded share image for a quote and returns it as a PNG: plum
// background, Rory's line drawing, a big sunshine quote mark, and the quote
// sized to fit.
export const renderQuoteImage = async (quote: RoryQuote): Promise<Blob> => {
  if (typeof document !== "undefined" && "fonts" in document) {
    try {
      await (document as any).fonts.load("800 72px 'Open Sans'");
      await (document as any).fonts.load("400 28px 'Open Sans'");
      await (document as any).fonts.ready;
    } catch {
      // The system font stands in.
    }
  }

  const sketch = await loadImage("/rory-quotes/rory-sketch.png");
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");
  const font = (weight: number, px: number) => `${weight} ${px}px 'Open Sans', system-ui, sans-serif`;

  ctx.fillStyle = BRAND.plum;
  ctx.fillRect(0, 0, W, H);

  const inset = 48;
  ctx.lineWidth = 4;
  ctx.strokeStyle = "rgba(245, 240, 225, 0.35)";
  roundRect(ctx, inset, inset, W - inset * 2, H - inset * 2, 28);
  ctx.stroke();

  const centerX = W / 2;
  const padX = 132;
  const contentW = W - padX * 2;

  const avatarR = 96;
  const avatarY = 220;
  ctx.save();
  ctx.beginPath();
  ctx.arc(centerX, avatarY, avatarR, 0, Math.PI * 2);
  ctx.fillStyle = BRAND.cream;
  ctx.fill();
  ctx.lineWidth = 5;
  ctx.strokeStyle = BRAND.sunshine;
  ctx.stroke();
  if (sketch) {
    ctx.clip();
    ctx.drawImage(sketch, centerX - avatarR, avatarY - avatarR, avatarR * 2, avatarR * 2);
  }
  ctx.restore();

  ctx.fillStyle = BRAND.sunshine;
  ctx.font = font(800, 200);
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.fillText("“", centerX, avatarY + avatarR + 188);

  const textTop = avatarY + avatarR + 230;
  const footerTop = H - 250;
  const underlineGap = 70;
  const maxTextH = footerTop - underlineGap - textTop;

  ctx.textBaseline = "top";
  let fontSize = 74;
  let lines: string[] = [];
  let lineHeight = 0;
  while (fontSize >= 34) {
    ctx.font = font(800, fontSize);
    lines = wrapLines(ctx, quote.text, contentW);
    lineHeight = fontSize * 1.3;
    if (lines.length * lineHeight <= maxTextH) break;
    fontSize -= 2;
  }
  ctx.font = font(800, fontSize);
  ctx.fillStyle = BRAND.cream;
  const blockH = lines.length * lineHeight;
  const blockStart = textTop + Math.max(0, (maxTextH - blockH) / 2);
  lines.forEach((line, i) => ctx.fillText(line, centerX, blockStart + i * lineHeight));

  const underlineY = Math.min(blockStart + blockH + 34, footerTop - 36);
  ctx.strokeStyle = BRAND.sunshine;
  ctx.lineWidth = 6;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(centerX - 48, underlineY);
  ctx.lineTo(centerX + 48, underlineY);
  ctx.stroke();

  ctx.fillStyle = BRAND.white;
  ctx.font = font(800, 36);
  ctx.fillText("RORY SUTHERLAND", centerX, footerTop + 30);
  if (quote.display_source) {
    // A long source wraps onto a second line rather than being cut short;
    // a very long one drops a size so two lines still hold it.
    ctx.fillStyle = "rgba(245, 240, 225, 0.65)";
    let sourceSize = 27;
    let sourceLines: string[] = [];
    for (;;) {
      ctx.font = font(400, sourceSize);
      sourceLines = wrapLines(ctx, quote.display_source, contentW);
      if (sourceLines.length <= 2 || sourceSize <= 20) break;
      sourceSize -= 2;
    }
    if (sourceLines.length > 2) {
      let second = sourceLines[1];
      while (second.length > 1 && ctx.measureText(`${second}\u2026`).width > contentW) second = second.slice(0, -1).trimEnd();
      sourceLines = [sourceLines[0], `${second}\u2026`];
    }
    sourceLines.forEach((line, i) => ctx.fillText(line, centerX, footerTop + 84 + i * (sourceSize * 1.3)));
  }

  ctx.fillStyle = BRAND.sunshine;
  ctx.font = font(800, 26);
  ctx.fillText("42COURSES.COM/RORY-QUOTES", centerX, H - 96);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Could not create image"))), "image/png");
  });
};
